import { useEffect, useState } from 'react';
import Calendar from '../Calendar/Calendar';
import { Container, Loader, LoaderWrapper, ErrorWrapper } from './MainStyle';
import { Overlay, Text, Transition } from '@mantine/core';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Event } from '../../interfaces/event';
import { Settings } from '../../interfaces/settings';
import { initialState } from '../../shared/initial-settings';
import { handleThemeClassName } from '../../shared/theme-class-name';

const Main = () => {
  const { shareId } = useParams();

  const [events, setEvents] = useState<Event[]>([]);
  const [settings, setSettings] = useState<Settings>(initialState);
  const [loading, setIsLoading] = useState(true);
  const [error, setError] = useState<number | null>(null);

  useEffect(() => {
    const handleInitialLoad = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_MONDAY_SERVER_URL}/monday/${shareId}`
        );
        if (!response.data) return;
        const { events, settings, theme } = response.data;
        handleThemeClassName(theme);
        setEvents(events);
        setSettings(settings);
        setIsLoading(false);
      } catch (error: any) {
        const status = error?.response?.status || 500;
        setIsLoading(false);
        setError(status);
      }
    };
    handleInitialLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderErrorView = () => {
    switch (error) {
      case 403:
        return (
          <ErrorWrapper>
            <Text size="24px" fw={600} c="var(--app-primary-text-color)">
              Calendar is unavailable
            </Text>
            <Text size="16px" c="gray">
              This calendar is temporarily unavailable. Please contact the person who sent you this
              link.
            </Text>
          </ErrorWrapper>
        );
      case 404:
        return (
          <ErrorWrapper>
            <Text size="24px" fw={600} c="var(--app-primary-text-color)">
              404 | Calendar Not Found
            </Text>
            <Text size="16px" c="gray">
              The calendar you're looking for doesn't exist.
            </Text>
          </ErrorWrapper>
        );
      default:
        return (
          <ErrorWrapper>
            <Text size="24px" fw={600} c="var(--app-primary-text-color)">
              Error Loading Calendar
            </Text>
            <Text size="16px" c="gray">
              An error occurred while loading the calendar. Please try again later.
            </Text>
          </ErrorWrapper>
        );
    }
  };

  return (
    <Container>
      <Transition mounted={loading || !!error} transition="fade" duration={300}>
        {(styles) => (
          <>
            <Overlay
              style={styles}
              color="#000"
              backgroundOpacity={0.1}
              blur={6}
              fixed={false}
              zIndex={99}
            />
            {error && renderErrorView()}
            {loading && (
              <LoaderWrapper>
                <Loader size={60} color="var(--primary-button-background-color)" />
                <Text size={'18px'}>Loading your calendar...</Text>
              </LoaderWrapper>
            )}
          </>
        )}
      </Transition>
      <Calendar events={events} settings={settings} />
    </Container>
  );
};

export { Main };
